import {
    useCallback,
    useState,
    useMemo,
    type FC
} from "react";
import {
    type LayoutChangeEvent,
    Platform,
    View
} from "react-native";
import type IBreadcrumbProps from "./type";
import type {
    BreadcrumbContextType
} from "./type";
import stylesheet from "./stylesheet";
import BreadcrumbEllipsis from "./components/ellipsis";
import BreadcrumbItem from "./components/item";
import BreadcrumbLink from "./components/link";
import BreadcrumbList from "./components/list";
import BreadcrumbPage from "./components/page";
import BreadcrumbSeparator from "./components/separator";
import {
    BreadcrumbContext
} from "./context";
import {
    NCoreUIKitLocalize
} from "../../core/hooks";
import {
    useBreakpoint
} from "../../helpers/pointer";

const Breadcrumb: FC<IBreadcrumbProps> = ({
    collapseBehaviour = "auto",
    compactBehaviour = "back",
    "aria-label": ariaLabel,
    itemsBeforeCollapse = 1,
    itemsAfterCollapse = 2,
    separator = "chevron",
    accessibilityLabel,
    size = "medium",
    customLocalize,
    maxItems = 4,
    customTheme,
    children,
    style,
    ...props
}) => {
    const {
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const breakpoint = useBreakpoint();

    const [
        availableWidth,
        setAvailableWidth
    ] = useState(0);

    const [
        fullWidth,
        setFullWidth
    ] = useState(0);

    const isCompact = Platform.OS !== "web" || breakpoint === "compact";

    const isOverflow = availableWidth > 0 && fullWidth > availableWidth;

    const handleFullWidthChange = useCallback((width: number) => {
        setFullWidth(Math.ceil(width));
    }, []);

    const contextValue = useMemo<BreadcrumbContextType>(() => ({
        handleFullWidthChange,
        itemsBeforeCollapse,
        itemsAfterCollapse,
        collapseBehaviour,
        compactBehaviour,
        isMeasure: false,
        customLocalize,
        customTheme,
        isOverflow,
        isCompact,
        separator,
        maxItems,
        size
    }), [
        handleFullWidthChange,
        itemsBeforeCollapse,
        itemsAfterCollapse,
        collapseBehaviour,
        compactBehaviour,
        customLocalize,
        customTheme,
        isOverflow,
        isCompact,
        separator,
        maxItems,
        size
    ]);

    const onLayout = (event: LayoutChangeEvent) => {
        setAvailableWidth(Math.floor(event.nativeEvent.layout.width));
    };

    if(isCompact && compactBehaviour === "hidden") {
        return null;
    }

    return <BreadcrumbContext.Provider
        value={contextValue}
    >
        <View
            {...props}
            aria-label={ariaLabel ?? accessibilityLabel ?? localize("breadcrumb-location")}
            style={[
                style,
                stylesheet.container
            ]}
            onLayout={onLayout}
            role="navigation"
        >
            {children}
        </View>
    </BreadcrumbContext.Provider>;
};
export default Object.assign(Breadcrumb, {
    Separator: BreadcrumbSeparator,
    Ellipsis: BreadcrumbEllipsis,
    Item: BreadcrumbItem,
    Link: BreadcrumbLink,
    List: BreadcrumbList,
    Page: BreadcrumbPage
});
